import { formatBytes } from '@/utils';
import FileExcelOutlined from '@ant-design/icons/FileExcelOutlined';
import FileTextOutlined from '@ant-design/icons/FileTextOutlined';
import Select from 'antd/es/select';
import Space from 'antd/es/space';
import Table from 'antd/es/table';
import Tag from 'antd/es/tag';
import Typography from 'antd/es/typography';
import type { RcFile } from 'antd/es/upload';
import React, { useEffect, useState } from 'react';
import { styled, useTheme } from 'styled-components';

const { Text } = Typography;

const FileInfoContainer = styled.div`
  ${({ theme }) => `
  background: ${theme.colorBgContainer};
  border: ${theme.lineWidth}px solid ${theme.colorBorder};
  border-radius: ${theme.borderRadiusLG}px;
  padding: ${theme.padding}px;
  margin: ${theme.margin}px 0;
`}
`;

const FileInfoRow = styled.div`
  ${({ theme }) => `
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.marginXS}px;

  &:last-child {
    margin-bottom: 0;
  }
`}
`;

const PreviewContainer = styled.div`
  ${({ theme }) => `
  margin-top: ${theme.marginMD}px;
  border: ${theme.lineWidth}px solid ${theme.colorBorder};
  border-radius: ${theme.borderRadius}px;
  overflow: hidden;
`}
`;

const PreviewHeader = styled.div`
  ${({ theme }) => `
  padding: ${theme.paddingSM}px ${theme.padding}px;
  background: ${theme.colorFillQuaternary};
  border-bottom: ${theme.lineWidth}px solid ${theme.colorBorder};
  font-weight: ${theme.fontWeightStrong};
`}
`;

interface PreviewDatasetProps {
  file: RcFile;
  fileType: 'csv' | 'xlsx';
}

interface DataPreview {
  headers: string[];
  rows: string[][];
  detectedTypes: Record<string, string>;
}

export const PreviewDataset: React.FC<PreviewDatasetProps> = ({
  file,
  fileType,
}) => {
  const theme = useTheme();
  const [currentFileType, setCurrentFileType] = useState<'csv' | 'xlsx'>(
    fileType,
  );
  const [dataPreview, setDataPreview] = useState<DataPreview | null>(null);

  // Auto-detect CSV delimiter
  const detectDelimiter = (text: string): string => {
    const sample = text.split('\n').slice(0, 5).join('\n'); // Check first 5 lines
    const delimiters = [',', ';', '\t', '|'];
    const counts = delimiters.map((delim) => ({
      delimiter: delim,
      count: (sample.match(new RegExp(`\\${delim}`, 'g')) || []).length,
    }));

    // Return the delimiter with the highest count
    const best = counts.reduce((prev, curr) =>
      curr.count > prev.count ? curr : prev,
    );
    return best.count > 0 ? best.delimiter : ',';
  };

  // Proper CSV parser that handles quoted fields with newlines and commas
  const parseCSV = React.useCallback(
    (text: string, delimiter?: string): string[][] => {
      const actualDelimiter = delimiter || detectDelimiter(text);
      const result: string[][] = [];
      let currentRow: string[] = [];
      let currentField = '';
      let inQuotes = false;
      let i = 0;

      while (i < text.length) {
        const char = text[i];
        const nextChar = text[i + 1];

        if (char === '"') {
          if (inQuotes && nextChar === '"') {
            // Escaped quote inside quoted field
            currentField += '"';
            i += 2;
            continue;
          } else {
            // Start or end of quoted field
            inQuotes = !inQuotes;
          }
        } else if (char === actualDelimiter && !inQuotes) {
          // Field separator
          currentRow.push(currentField.trim());
          currentField = '';
        } else if ((char === '\n' || char === '\r') && !inQuotes) {
          // Row separator
          if (currentField || currentRow.length > 0) {
            currentRow.push(currentField.trim());
            if (currentRow.some((field) => field.length > 0)) {
              result.push(currentRow);
            }
            currentRow = [];
            currentField = '';
          }
          // Skip \r\n sequence
          if (char === '\r' && nextChar === '\n') {
            i++;
          }
        } else {
          currentField += char;
        }
        i++;
      }

      // Handle last field/row
      if (currentField || currentRow.length > 0) {
        currentRow.push(currentField.trim());
        if (currentRow.some((field) => field.length > 0)) {
          result.push(currentRow);
        }
      }

      return result;
    },
    [],
  );

  // Helper function to generate data preview
  const generateDataPreview = React.useCallback(
    async (file: File, fileType: 'csv' | 'xlsx'): Promise<DataPreview> => {
      if (fileType === 'csv') {
        const text = await file.text();
        const parsedRows = parseCSV(text);

        if (parsedRows.length === 0) {
          throw new Error('No data found in CSV file');
        }

        if (parsedRows.length === 1) {
          throw new Error('Only header row found, no data rows detected');
        }

        if (parsedRows[0].length === 1) {
          throw new Error(
            'Only one column detected. Check if the delimiter is correct.',
          );
        }

        const headers = parsedRows[0] || [];
        const rows = parsedRows.slice(1, 6); // Show first 5 data rows

        // Enhanced type detection
        const detectedTypes: Record<string, string> = {};
        headers.forEach((header, index) => {
          const sampleValues = rows
            .map((row) => row[index])
            .filter((val) => val && val.trim() !== '')
            .slice(0, 10); // Check first 10 non-empty values

          if (sampleValues.length === 0) {
            detectedTypes[header] = 'string';
            return;
          }

          // Check for boolean values
          const booleanValues = ['true', 'false', 'yes', 'no', '1', '0'];
          const isBooleanLike = sampleValues.every((val) =>
            booleanValues.includes(val.toLowerCase().trim()),
          );

          if (isBooleanLike) {
            detectedTypes[header] = 'boolean';
            return;
          }

          // Check for numeric values (including decimals)
          const isNumeric = sampleValues.every((val) => {
            const num = parseFloat(val.replace(/[,$%]/g, ''));
            return !isNaN(num) && isFinite(num);
          });

          if (isNumeric) {
            // Distinguish between integer and float
            const hasDecimals = sampleValues.some((val) => {
              const cleaned = val.replace(/[,$%]/g, '');
              return cleaned.includes('.') && !cleaned.endsWith('.0');
            });
            detectedTypes[header] = hasDecimals ? 'float' : 'integer';
            return;
          }

          // Check for date values
          const isDate = sampleValues.some((val) => {
            const date = new Date(val);
            return !isNaN(date.getTime()) && val.length > 4; // Avoid detecting years as dates
          });

          if (isDate) {
            detectedTypes[header] = 'datetime';
            return;
          }

          // Default to string
          detectedTypes[header] = 'string';
        });

        return {
          headers,
          rows,
          detectedTypes,
        };
      }

      // For Excel files, provide basic info
      return {
        headers: ['Excel file detected'],
        rows: [['Full preview available after upload']],
        detectedTypes: {},
      };
    },
    [parseCSV],
  );

  // Generate preview when file or file type changes
  useEffect(() => {
    const loadPreview = async () => {
      try {
        const preview = await generateDataPreview(file, currentFileType);
        setDataPreview(preview);
      } catch (error) {
        setDataPreview(null);
      }
    };

    void loadPreview();
  }, [file, currentFileType, generateDataPreview]);

  const renderPreviewTable = (dataPreview: DataPreview) => {
    const columns = dataPreview.headers.map((header) => ({
      title: (
        <Space>
          <Text strong>{header}</Text>
          <Tag
            color={
              dataPreview.detectedTypes[header] === 'integer'
                ? 'blue'
                : dataPreview.detectedTypes[header] === 'float'
                  ? 'cyan'
                  : dataPreview.detectedTypes[header] === 'boolean'
                    ? 'purple'
                    : dataPreview.detectedTypes[header] === 'datetime'
                      ? 'orange'
                      : 'green'
            }
          >
            {dataPreview.detectedTypes[header] || 'string'}
          </Tag>
        </Space>
      ),
      dataIndex: header,
      key: header,
      ellipsis: true,
    }));

    const dataSource = dataPreview.rows.map((row, index) => {
      const rowData: Record<string, string> = { key: index.toString() };
      dataPreview.headers.forEach((header, colIndex) => {
        rowData[header] = row[colIndex] || '';
      });
      return rowData;
    });

    return (
      <PreviewContainer>
        <PreviewHeader>
          <Space>
            <Text strong>Data Preview</Text>
            <Text type="secondary">
              ({dataPreview.rows.length} rows detected)
            </Text>
          </Space>
        </PreviewHeader>
        <Table
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          size="small"
          scroll={{ x: 'max-content' }}
        />
      </PreviewContainer>
    );
  };

  return (
    <>
      <FileInfoContainer>
        <FileInfoRow>
          <Space>
            {currentFileType === 'xlsx' ? (
              <FileExcelOutlined />
            ) : (
              <FileTextOutlined />
            )}
            <Text strong>{file.name}</Text>
          </Space>
          <Text type="secondary">{formatBytes(file.size)}</Text>
        </FileInfoRow>
        <FileInfoRow>
          <Text>File Type:</Text>
          <Select
            value={currentFileType}
            onChange={setCurrentFileType}
            style={{ width: `${theme.controlHeightLG * 3}px` }}
            size="small"
            options={[
              { label: 'CSV', value: 'csv' },
              { label: 'Excel', value: 'xlsx' },
            ]}
          />
        </FileInfoRow>
      </FileInfoContainer>

      {dataPreview && renderPreviewTable(dataPreview)}
    </>
  );
};

export default PreviewDataset;
